/* nplus-buybox.js — <nplus-buybox> controller.
   Pill variant picker (syncs hidden select + price), Add-to-cart bundle (bike + gift + accessories),
   and the exclusive deposit/leasing accordion (wired in a later task). Classic script. */
(function () {
  'use strict';

  class NplusBuybox extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.select = this.querySelector('[data-nplus-bike-variant]');
      this.priceEl = this.querySelector('[data-nplus-price]');
      this.atc = this.querySelector('[data-nplus-atc]');
      this.errorEl = this.querySelector('[data-nplus-error]');
      this.gift = this.querySelector('nplus-gift-bundle');
      this.stockEl = this.querySelector('[data-nplus-stock]');
      this.stockTextEl = this.querySelector('[data-nplus-stock-text]');

      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (pill) {
        pill.addEventListener('click', function () { self.selectPill(pill); });
      });
      if (this.atc) this.atc.addEventListener('click', this.onAdd.bind(this));

      // Cash / Finance "buy now" buttons: add the selection, go straight to checkout.
      this.buyNow = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-buynow]'));
      this.buyNow.forEach(function (btn) {
        btn.addEventListener('click', function () { self.onBuyNow(btn.getAttribute('data-method')); });
      });

      this.initReveals(); // accordion (defined below; no-op if no reveal buttons)
      this.initTabs();    // Cash/Lease/Finance switcher (no-op if no tabs)
      this.initLeaseTeaser(); // price→Lease-tab teaser link(s) (no-op if none rendered)
      this.initMoney(); // Intl formatter for the subtotal, from .nplus-bb__i18n's currency/locale

      // Subtotal recompute triggers: bike-variant change (pill click, below) and accessory
      // selection change (dispatched by nplus-accessories.js). Bound before the initial
      // emitBikeVariant()/recalcSubtotal() calls so nothing fired during setup is missed.
      document.addEventListener('nplus:bike-variant-change', function () { self.recalcSubtotal(); });
      document.addEventListener('nplus:accessories-change', function () { self.recalcSubtotal(); });

      this.emitBikeVariant(); // initial bike size → size-linked accessories
      this.recalcSubtotal();  // initial subtotal paint, once listeners are bound
    }

    selectPill(pill) {
      if (pill.getAttribute('aria-disabled') === 'true') return;
      var id = pill.getAttribute('data-variant');
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-variant-pill]'), function (p) {
        p.classList.toggle('is-selected', p === pill);
      });
      if (this.select) { this.select.value = id; }
      if (this.priceEl) {
        this.priceEl.textContent = pill.getAttribute('data-price') || this.priceEl.textContent;
        var minor = pill.getAttribute('data-price-minor');
        if (minor != null) this.priceEl.setAttribute('data-price-minor', minor);
      }
      this.updateStock(id);
      this.clearError();
      this.emitBikeVariant(); // dispatches nplus:bike-variant-change → recalcSubtotal via listener above
    }

    // Notify size-linked accessories of the current bike size. Position is locale-stable
    // (variant.position), so accessories match on it rather than translated titles.
    // Cache on window covers either custom element connecting first.
    emitBikeVariant() {
      var pill = this.querySelector('[data-nplus-variant-pill].is-selected');
      var variantId = pill ? pill.getAttribute('data-variant') : (this.select ? this.select.value : null);
      var position = pill ? parseInt(pill.getAttribute('data-position'), 10) : 1;
      window.nplusBikePosition = position;
      document.dispatchEvent(new CustomEvent('nplus:bike-variant-change', { detail: { variantId: variantId, position: position } }));
    }

    setBusy(b) { if (this.atc) { this.atc.disabled = b; this.atc.setAttribute('aria-busy', b ? 'true' : 'false'); } }
    showError(m) { if (this.errorEl) { this.errorEl.textContent = m; this.errorEl.hidden = false; } }
    clearError() { if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.hidden = true; } }

    onAdd() {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.addBundle !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      this.setBusy(true);
      var self = this;
      window.nplusCart.addBundle({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories })
        .catch(function (err) {
          var sold = /no longer available|sold out/i.test(err.message || '');
          self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
        })
        .then(function () { self.setBusy(false); });
    }

    // Cash/Finance "buy now": add bike + gift + accessories, then go straight to checkout
    // (no cart flyover). Both buttons (card/Klarna) land on the same checkout; Klarna is a
    // payment method there. `method` is a label/analytics hint only.
    onBuyNow(method) {
      this.clearError();
      var bikeVariantId = this.select ? this.select.value : null;
      if (!bikeVariantId) { this.showError(this.t('select_option', 'Please select an option.')); return; }
      if (!window.nplusCart || typeof window.nplusCart.buildBundleItems !== 'function') { this.showError(this.t('cart_unavailable', 'Cart unavailable. Please refresh.')); return; }

      var gift = this.gift ? this.gift.getSelection() : { included: false, variantId: null };
      if (gift.included && !gift.variantId) { this.showError(this.t('choose_gift_size', 'Please choose a gift size.')); return; }

      var accessories = (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
        ? window.nplusAccessories.getSelection() : [];

      var items = window.nplusCart.buildBundleItems({ bikeVariantId: bikeVariantId, gift: gift, accessories: accessories });
      var self = this;
      this.setBuyNowBusy(true);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error((e && e.description) || 'add failed'); });
        return r.json();
      }).then(function () {
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        window.location.assign(root + 'checkout');
      }).catch(function (err) {
        self.setBuyNowBusy(false);
        var sold = /no longer available|sold out/i.test(err.message || '');
        self.showError(sold ? self.t('gift_unavailable', 'That gift is no longer available. Deselect it to add the bike on its own.') : self.t('add_failed', 'Sorry, we could not add this to your cart. Please try again.'));
      });
    }

    setBuyNowBusy(b) {
      (this.buyNow || []).forEach(function (btn) { btn.disabled = b; btn.setAttribute('aria-busy', b ? 'true' : 'false'); });
    }

    // Cash / Lease / Finance tab switcher. No-op on the reference buy-box (no tabs).
    initTabs() {
      var self = this;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-tab]'));
      if (!this.tabs.length) return;
      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { self.selectTab(tab.getAttribute('data-nplus-tab')); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : self.tabs.length - 1)) % self.tabs.length;
          self.tabs[next].focus();
          self.selectTab(self.tabs[next].getAttribute('data-nplus-tab'));
        });
      });
    }

    selectTab(name) {
      this.tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-nplus-tab') === name;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.classList.toggle('is-active', on);
        tab.tabIndex = on ? 0 : -1;
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-tabpanel]'), function (p) {
        p.hidden = p.getAttribute('data-nplus-tabpanel') !== name;
      });
      // Stripe is mounted on reveal (togglePanel), not on tab-select — the Lease/Finance
      // forms sit behind an "Order with…" reveal, so mounting here would mount while hidden.
      this.clearError();
    }

    // Lazily parses .nplus-bb__i18n (strings + currency/locale for the subtotal formatter).
    _loadI18n() {
      if (!this._i18n) {
        try {
          var el = this.querySelector('.nplus-bb__i18n');
          this._i18n = el ? JSON.parse(el.textContent) : {};
        } catch (e) { this._i18n = {}; }
      }
      return this._i18n;
    }

    // i18n helper — reads optional cfg.i18n injected by Liquid, else English fallback.
    t(key, fallback) {
      return this._loadI18n()[key] || fallback;
    }

    // Lazily parses .nplus-bb__stock-map (variant-id → inventory_quantity, null when untracked,
    // plus the low-stock threshold). No-op container when the stock counter block is off.
    _loadStockMap() {
      if (!this._stockMap) {
        try {
          var el = this.querySelector('.nplus-bb__stock-map');
          this._stockMap = el ? JSON.parse(el.textContent) : { threshold: 0, quantities: {} };
        } catch (e) { this._stockMap = { threshold: 0, quantities: {} }; }
      }
      return this._stockMap;
    }

    // Refreshes the stock line for the given variant id on pill switch. Mirrors the Liquid
    // rules in nplus-buybox-combined.liquid: hidden when untracked or qty <= 0, low-stock
    // message (with count) at/under the threshold, in-stock message above it.
    updateStock(variantId) {
      if (!this.stockEl) return;
      var map = this._loadStockMap();
      var qty = map.quantities[variantId];
      if (qty === null || qty === undefined || qty <= 0) {
        this.stockEl.hidden = true;
        return;
      }
      var i18n = this._loadI18n();
      if (this.stockTextEl) {
        this.stockTextEl.textContent = qty <= map.threshold
          ? (i18n.stock_low || '').replace('__COUNT__', qty)
          : (i18n.stock_in || '');
      }
      this.stockEl.hidden = false;
    }

    // Teaser link(s) between price and variant pills: jump straight to the Lease tab and
    // scroll the "Order with Company Bike Lease" trigger to the top of the viewport. Bound
    // page-wide (not just inside this element) so the near-title teaser above the buy-box
    // (outside <nplus-buybox>, rendered by main-product.liquid) reuses this same handler.
    // No-op unless a Lease tab actually exists — Liquid gates every teaser link on the same
    // show_lease condition as the tab, but this guards against any drift between the two.
    initLeaseTeaser() {
      var self = this;
      if (!this.querySelector('[data-nplus-tab="lease"]')) return;
      var links = Array.prototype.slice.call(document.querySelectorAll('[data-nplus-lease-teaser]'));
      links.forEach(function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          self.selectTab('lease');
          var trigger = self.querySelector('[data-nplus-reveal="lease"]');
          if (trigger && trigger.getAttribute('aria-expanded') !== 'true') self.togglePanel('lease');
          if (trigger) trigger.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    // Sets up the Intl.NumberFormat used by recalcSubtotal(), from the currency/locale that
    // Liquid injects into .nplus-bb__i18n (same pattern as nplus-leasing-calc.js).
    initMoney() {
      var cfg = this._loadI18n();
      var currency = cfg.currency || 'EUR';
      var locale = cfg.locale || undefined;
      try { this.money = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }); }
      catch (e) { this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }); }
      this.decimals = this.money.resolvedOptions().maximumFractionDigits;
    }

    // Live subtotal = selected variant price + checked accessories (free items contribute 0).
    // Reads the base price from priceEl's own data-price-minor (kept current by selectPill()),
    // not from the pills, since single-variant products render no pills at all.
    recalcSubtotal() {
      if (!this.priceEl || !this.money) return;
      var baseMinor = parseInt(this.priceEl.getAttribute('data-price-minor'), 10);
      if (isNaN(baseMinor)) baseMinor = 0;
      var accMinor = 0;
      if (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function') {
        window.nplusAccessories.getSelection().forEach(function (item) {
          accMinor += item.free ? 0 : (Number(item.priceMinor) || 0);
        });
      }
      var totalMinor = baseMinor + accMinor;
      this.priceEl.textContent = this.money.format(totalMinor / Math.pow(10, this.decimals));
    }

    initReveals() {
      var self = this;
      this.reveals = Array.prototype.slice.call(this.querySelectorAll('[data-nplus-reveal]'));
      this.reveals.forEach(function (btn) {
        btn.addEventListener('click', function () { self.togglePanel(btn.getAttribute('data-nplus-reveal')); });
      });
    }

    togglePanel(name) {
      var self = this;
      var willOpen = false;
      this.reveals.forEach(function (btn) {
        var n = btn.getAttribute('data-nplus-reveal');
        var panel = self.querySelector('[data-nplus-panel="' + n + '"]');
        var open = (n === name) && !(btn.getAttribute('aria-expanded') === 'true');
        if (n === name) willOpen = open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });
      if (willOpen) {
        var openPanel = this.querySelector('[data-nplus-panel="' + name + '"]');
        var dep = openPanel && (openPanel.querySelector('nplus-leasing-deposit') || openPanel.querySelector('nplus-card-deposit'));
        if (dep && typeof dep.ensureStripe === 'function') dep.ensureStripe();
      }
    }
  }

  if (!customElements.get('nplus-buybox')) {
    customElements.define('nplus-buybox', NplusBuybox);
  }
})();
